// src/pages/QuickTrade/QuickTrade.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { alpacaService, Position, Account, TradeAnalysis, SecondOpinion } from '../../services/AlpacaService';
import { useAccountMode } from '../../context/AccountModeContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useDemo } from '../../context/DemoContext';
import { DEMO_TRADE_ANALYSIS, DEMO_SECOND_OPINION } from '../../services/demoData';
import TICKER_DESC from '../../data/tickerDescriptions';
import styles from './QuickTrade.module.css';

/* ── Popular ticker groups ── */

const QUICK_TICKERS: Record<string, { label: string; tickers: string[] }> = {
  energy: { label: '⛽ Energy', tickers: ['XLE', 'USO', 'UCO', 'XOP', 'OIH'] },
  broad: { label: '📊 Broad', tickers: ['SPY', 'QQQ', 'DIA', 'IWM'] },
  sectors: { label: '🏭 Sectors', tickers: ['XLK', 'XLV', 'XLF', 'XLI', 'XLU', 'XLY'] },
  safe: { label: '🛡️ Safe Haven', tickers: ['GLD', 'SLV', 'TLT', 'VXX'] },
  tech: { label: '💻 Tech', tickers: ['AAPL', 'MSFT', 'NVDA', 'GOOGL', 'META', 'AMZN'] },
  defense: { label: '🎯 Defense', tickers: ['LMT', 'RTX', 'NOC', 'GD', 'BA', 'HII'] },
  shipping: { label: '🚢 Shipping', tickers: ['FRO', 'STNG', 'DHT', 'INSW', 'ZIM'] },
  lng: { label: '🔥 LNG', tickers: ['LNG', 'CQP', 'AR', 'EQT', 'GLNG'] },
  metals: { label: '⛏ Metals', tickers: ['NEM', 'GOLD', 'GFI', 'AA', 'CENX', 'GDX'] },
  fertilizer: { label: '🌾 Fertilizer', tickers: ['MOS', 'NTR', 'CF'] },
};

/* ── Component ── */

const QuickTrade: React.FC = () => {
  const { mode, isLive } = useAccountMode();
  const { isAdmin } = useAuth();
  const { addToast } = useToast();
  const { isDemoMode, exitDemo } = useDemo();
  const navigate = useNavigate();

  // Ticker + price
  const [ticker, setTicker] = useState('');
  const [price, setPrice] = useState<number | null>(null);
  const [change, setChange] = useState<number | null>(null);
  const [fetchingPrice, setFetchingPrice] = useState(false);
  const [priceError, setPriceError] = useState('');
  const priceTimer = useRef<ReturnType<typeof setTimeout>>();

  // Order config — 3 distinct actions
  type TradeAction = 'buy' | 'sell' | 'short';
  const [action, setAction] = useState<TradeAction>('buy');
  const side: 'buy' | 'sell' = action === 'buy' ? 'buy' : 'sell'; // Alpaca only knows buy/sell
  const [orderType, setOrderType] = useState<'notional' | 'qty'>('notional');
  const [tradeType, setTradeType] = useState<'market' | 'limit' | 'stop' | 'stop_limit' | 'trailing_stop'>('market');
  const [limitPrice, setLimitPrice] = useState<number>(0);
  const [stopPrice, setStopPrice] = useState<number>(0);
  const [trailMode, setTrailMode] = useState<'percent' | 'price'>('percent');
  const [trailValue, setTrailValue] = useState<number>(0);
  const [amount, setAmount] = useState(500);
  const [qty, setQty] = useState(10);

  // Confirmation
  const [confirming, setConfirming] = useState(false);
  const [liveChecked, setLiveChecked] = useState(false);
  const [orderInProgress, setOrderInProgress] = useState(false);
  const [attachBracket, setAttachBracket] = useState(true);

  // Short confirmation modal (extra safety for live shorts)
  const [showShortConfirm, setShowShortConfirm] = useState(false);
  const [shortConfirmTicker, setShortConfirmTicker] = useState('');

  // Claude analysis
  const [analysis, setAnalysis] = useState<TradeAnalysis | null>(null);
  const [analyzingTrade, setAnalyzingTrade] = useState(false);

  // GPT second opinion
  const [secondOpinion, setSecondOpinion] = useState<SecondOpinion | null>(null);
  const [fetchingOpinion, setFetchingOpinion] = useState(false);

  // Counter-trade analysis (what if I shorted instead?)
  const [counterAnalysis, setCounterAnalysis] = useState<TradeAnalysis | null>(null);
  const [counterOpinion, setCounterOpinion] = useState<SecondOpinion | null>(null);
  const [fetchingCounter, setFetchingCounter] = useState(false);

  // Account + positions
  const [account, setAccount] = useState<Account | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();
  const showToast = (msg: string, type: 'success' | 'error') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  };

  // Fetch price when ticker changes (debounced)
  const fetchPrice = useCallback(
    async (sym: string) => {
      if (!sym || sym.length < 1) {
        setPrice(null);
        setChange(null);
        setPriceError('');
        return;
      }
      setFetchingPrice(true);
      setPriceError('');
      try {
        const res = await alpacaService.getSnapshot(sym);
        if (res.snapshots && res.snapshots[sym]) {
          const snap = res.snapshots[sym];
          setPrice(snap.price);
          setChange(snap.change_pct ?? null);
        } else {
          setPriceError('Ticker not found');
          setPrice(null);
          setChange(null);
        }
      } catch {
        setPriceError('Failed to fetch price');
        setPrice(null);
        setChange(null);
      } finally {
        setFetchingPrice(false);
      }
    },
    [mode],
  );

  useEffect(() => {
    if (priceTimer.current) clearTimeout(priceTimer.current);
    if (!ticker.trim()) {
      setPrice(null);
      setChange(null);
      return;
    }
    priceTimer.current = setTimeout(() => fetchPrice(ticker.trim()), 400);
    return () => {
      if (priceTimer.current) clearTimeout(priceTimer.current);
    };
  }, [ticker, fetchPrice]);

  // Auto-refresh price every 5s when we have a ticker
  useEffect(() => {
    if (!ticker.trim() || !price) return;
    const id = setInterval(() => fetchPrice(ticker.trim()), 5000);
    return () => clearInterval(id);
  }, [ticker, price, fetchPrice]);

  // When switching to a price-aware order type, default the price to the
  // current snapshot so the user has a sensible starting point instead of $0.
  useEffect(() => {
    if (!price) return;
    if ((tradeType === 'limit' || tradeType === 'stop_limit') && limitPrice === 0) {
      setLimitPrice(price);
    }
    if ((tradeType === 'stop' || tradeType === 'stop_limit') && stopPrice === 0) {
      setStopPrice(price);
    }
    if (tradeType === 'trailing_stop' && trailValue === 0) {
      setTrailValue(trailMode === 'percent' ? 2.0 : 1.0);
    }
  }, [tradeType, price]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch account + positions
  const fetchAccountData = useCallback(async () => {
    try {
      const [acct, posData] = await Promise.all([alpacaService.getAccount(mode), alpacaService.getPositions(mode)]);
      setAccount(acct);
      setPositions(posData.positions || []);
    } catch (err: any) {
      addToast(err.message || 'Failed to load account data', 'error');
    }
  }, [mode]);

  useEffect(() => {
    fetchAccountData();
    const id = setInterval(fetchAccountData, 30_000);
    return () => clearInterval(id);
  }, [fetchAccountData]);

  // ── Deep-link support: /quick-trade?action=close&symbol=SPY&qty=100 ──
  // Extended: screener can pass &plan=ai&sl=X&tp=Y&hold=N&tgtPct=M to surface
  // the AI's suggested plan as read-only context above the form.
  const [searchParams, setSearchParams] = useSearchParams();
  const [deepLinkAction, setDeepLinkAction] = useState<string | null>(null);
  const [aiPlan, setAiPlan] = useState<{
    stopLoss: number | null;
    takeProfit: number | null;
    holdDays: number | null;
    targetPct: number | null;
    action: string;
    symbol: string;
  } | null>(null);

  useEffect(() => {
    const action = searchParams.get('action');
    const symbol = searchParams.get('symbol');
    const deepQty = searchParams.get('qty');
    const plan = searchParams.get('plan');
    const sl = searchParams.get('sl');
    const tp = searchParams.get('tp');
    const hold = searchParams.get('hold');
    const tgtPct = searchParams.get('tgtPct');

    if (action && symbol) {
      setDeepLinkAction(action);
      setTicker(symbol.toUpperCase());
      if (action === 'close' || action === 'sell') {
        setAction('sell');
      } else if (action === 'short') {
        setAction('short');
      } else if (action === 'buy') {
        setAction('buy');
      }
      if (deepQty) {
        setOrderType('qty');
        setQty(parseInt(deepQty, 10) || 10);
      }
      if (plan === 'ai' && (sl || tp || hold || tgtPct)) {
        setAiPlan({
          stopLoss: sl ? parseFloat(sl) : null,
          takeProfit: tp ? parseFloat(tp) : null,
          holdDays: hold ? parseInt(hold, 10) : null,
          targetPct: tgtPct ? parseFloat(tgtPct) : null,
          action,
          symbol: symbol.toUpperCase(),
        });
      }
      // Clear URL params after reading (don't leave them in the URL)
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Select a quick ticker
  const selectTicker = (sym: string) => {
    setTicker(sym);
    setConfirming(false);
    setLiveChecked(false);
    setAnalysis(null);
    setSecondOpinion(null);
    setCounterAnalysis(null);
    setCounterOpinion(null);
  };

  const handleAskClaude = async () => {
    if (!ticker.trim()) return;
    setAnalyzingTrade(true);
    setAnalysis(null);
    setSecondOpinion(null);
    setCounterAnalysis(null);
    setCounterOpinion(null);
    if (isDemoMode) {
      await new Promise((r) => setTimeout(r, 1500));
      setAnalysis(DEMO_TRADE_ANALYSIS);
      setAnalyzingTrade(false);
      return;
    }
    try {
      const posInfo = currentPosition
        ? `${currentPosition.side}:${currentPosition.qty}@${currentPosition.avg_entry_price}`
        : '';
      const result = await alpacaService.getTradeAnalysis(ticker.trim(), side, action, posInfo);
      setAnalysis(result);
    } catch (e: any) {
      showToast(e.message || 'Claude analysis failed', 'error');
    } finally {
      setAnalyzingTrade(false);
    }
  };

  const handleSecondOpinion = async () => {
    if (!analysis || !ticker.trim()) return;
    setFetchingOpinion(true);
    setSecondOpinion(null);
    if (isDemoMode) {
      await new Promise((r) => setTimeout(r, 1000));
      setSecondOpinion(DEMO_SECOND_OPINION);
      setFetchingOpinion(false);
      return;
    }
    try {
      const result = await alpacaService.getSecondOpinion(ticker.trim(), side, price?.toString() || '', analysis);
      setSecondOpinion(result);
    } catch (e: any) {
      showToast(e.message || 'GPT second opinion failed', 'error');
    } finally {
      setFetchingOpinion(false);
    }
  };

  const handleCounterTrade = async () => {
    if (!ticker.trim()) return;
    setFetchingCounter(true);
    setCounterAnalysis(null);
    setCounterOpinion(null);
    try {
      const counterSide = action === 'buy' ? 'sell' : 'buy';
      const counterAction = action === 'buy' ? 'short' : 'buy';
      // Get Claude's take on the opposite trade
      const claudeResult = await alpacaService.getTradeAnalysis(ticker.trim(), counterSide, counterAction);
      setCounterAnalysis(claudeResult);
      // Get GPT's take on Claude's counter analysis
      try {
        const gptResult = await alpacaService.getSecondOpinion(
          ticker.trim(),
          counterSide,
          price?.toString() || '',
          claudeResult,
        );
        setCounterOpinion(gptResult);
      } catch {
        // GPT failure is non-critical — still show Claude's counter
      }
    } catch (e: any) {
      showToast(e.message || 'Counter-trade analysis failed', 'error');
    } finally {
      setFetchingCounter(false);
    }
  };

  // Gate: require extra confirmation for live short orders
  const handleExecuteGated = () => {
    if (action === 'short' && isLive) {
      setShortConfirmTicker('');
      setShowShortConfirm(true);
      return;
    }
    handleExecute();
  };

  // Place order
  const handleExecute = async () => {
    if (!ticker.trim()) return;
    setOrderInProgress(true);
    try {
      // Alpaca doesn't support notional for sell/short orders — convert $ to shares
      let sellQtyOverride: number | undefined;
      if (orderType === 'notional' && side === 'sell') {
        sellQtyOverride = price ? Math.floor(amount / price) : 0;
        if (sellQtyOverride < 1) {
          showToast('Amount too small — need at least 1 whole share to short', 'error');
          setOrderInProgress(false);
          return;
        }
      }
      // Attach AI bracket when user enabled the toggle AND we have a plan
      // AND the current side matches the plan's direction (long plan for buy,
      // short plan for sell). Mismatches fall back to a plain order.
      const bracketable =
        !!aiPlan &&
        attachBracket &&
        ((side === 'buy' && aiPlan.action === 'buy') ||
          (side === 'sell' && (aiPlan.action === 'sell' || aiPlan.action === 'short' || aiPlan.action === 'close')));
      const bracketFields =
        bracketable && aiPlan
          ? {
              ...(aiPlan.stopLoss != null ? { stop_loss: aiPlan.stopLoss } : {}),
              ...(aiPlan.takeProfit != null ? { take_profit: aiPlan.takeProfit } : {}),
              time_in_force: 'gtc' as const,
            }
          : {};

      const res = await alpacaService.placeOrder({
        symbol: ticker.trim(),
        side,
        ...(sellQtyOverride != null
          ? { qty: sellQtyOverride }
          : orderType === 'notional'
            ? { notional: amount }
            : { qty }),
        order_type: tradeType,
        ...(tradeType === 'limit' ? { limit_price: limitPrice } : {}),
        ...(tradeType === 'stop' ? { stop_price: stopPrice } : {}),
        ...(tradeType === 'stop_limit' ? { stop_price: stopPrice, limit_price: limitPrice } : {}),
        ...(tradeType === 'trailing_stop'
          ? trailMode === 'percent'
            ? { trail_percent: trailValue }
            : { trail_price: trailValue }
          : {}),
        ...bracketFields,
        mode,
      });
      if (res.success) {
        const filledPrice = res.order?.filled_avg_price
          ? ` @ $${parseFloat(res.order.filled_avg_price).toFixed(2)}`
          : '';
        showToast(
          `${actionLabel.toUpperCase()} ${ticker} — ${res.order?.status || 'submitted'}${filledPrice}`,
          'success',
        );
        setConfirming(false);
        setLiveChecked(false);
        fetchAccountData();
      } else {
        showToast(res.error || 'Order failed', 'error');
      }
    } catch (e: any) {
      showToast(e.message || 'Order failed', 'error');
    } finally {
      setOrderInProgress(false);
    }
  };

  // Close position
  const handleClose = async (sym: string) => {
    try {
      await alpacaService.closePosition(sym, mode);
      showToast(`Closed ${sym}`, 'success');
      fetchAccountData();
    } catch (e: any) {
      showToast(e.message || 'Close failed', 'error');
    }
  };

  // Position awareness — find current position for selected ticker
  const currentPosition = positions.find((p) => p.symbol === ticker.trim());
  const positionSide = currentPosition?.side; // 'long' or 'short' or undefined
  const positionQty = currentPosition ? parseFloat(currentPosition.qty) : 0;

  // Auto-select action based on position when ticker changes
  useEffect(() => {
    if (!ticker.trim()) return;
    const pos = positions.find((p) => p.symbol === ticker.trim());
    if (pos?.side === 'long') {
      setAction('sell'); // Default to close if they hold long
    } else if (!pos) {
      setAction('buy'); // Default to buy if no position
    }
    // Don't auto-change if they already picked an action
  }, [ticker]); // eslint-disable-line react-hooks/exhaustive-deps

  const estimatedShares =
    price && orderType === 'notional'
      ? side === 'sell'
        ? Math.floor(amount / price).toString()
        : (amount / price).toFixed(1)
      : null;
  const estimatedCost = price && orderType === 'qty' ? (qty * price).toFixed(0) : null;

  const actionLabel = action === 'buy' ? 'Buy' : action === 'sell' ? 'Close Long' : 'Short';
  const actionDescription =
    action === 'buy'
      ? `Open a new LONG position in ${ticker}`
      : action === 'sell'
        ? `CLOSE your ${Math.abs(positionQty)} share LONG position in ${ticker}`
        : `Open a new SHORT position in ${ticker}`;

  return (
    <section className={styles.page}>
      {aiPlan && (
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(52, 211, 153, 0.1))',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
          }}
        >
          <div
            style={{
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              opacity: 0.7,
              marginBottom: '6px',
              textTransform: 'uppercase',
            }}
          >
            AI Plan from Screener · {aiPlan.symbol} · {aiPlan.action.toUpperCase()}
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '14px',
              fontSize: '0.9rem',
              alignItems: 'baseline',
            }}
          >
            {aiPlan.stopLoss != null && (
              <span>
                <span style={{ opacity: 0.6, fontSize: '0.78rem' }}>Stop </span>
                <strong style={{ color: '#e74c3c' }}>${aiPlan.stopLoss.toFixed(2)}</strong>
              </span>
            )}
            {aiPlan.takeProfit != null && (
              <span>
                <span style={{ opacity: 0.6, fontSize: '0.78rem' }}>Target </span>
                <strong style={{ color: '#27ae60' }}>${aiPlan.takeProfit.toFixed(2)}</strong>
              </span>
            )}
            {aiPlan.targetPct != null && (
              <span>
                <span style={{ opacity: 0.6, fontSize: '0.78rem' }}>Goal </span>
                <strong style={{ color: '#27ae60' }}>+{aiPlan.targetPct}%</strong>
              </span>
            )}
            {aiPlan.holdDays != null && (
              <span>
                <span style={{ opacity: 0.6, fontSize: '0.78rem' }}>Hold </span>
                <strong>{aiPlan.holdDays}d</strong>
              </span>
            )}
          </div>
          {(aiPlan.stopLoss != null || aiPlan.takeProfit != null) && (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '10px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={attachBracket}
                onChange={(e) => setAttachBracket(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span>
                <strong>Attach as bracket order</strong> — submits entry + stop + target together. Time-in-force becomes
                GTC so the bracket survives the session.
              </span>
            </label>
          )}
          <div style={{ fontSize: '0.72rem', opacity: 0.6, marginTop: '8px' }}>
            Suggested by Claude — review before submitting. With the bracket toggle on, SL and TP go to the broker
            attached to this entry. Turn off for a plain order you'll manage yourself.
          </div>
        </div>
      )}
      {isDemoMode && (
        <div
          style={{
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            padding: '10px 20px',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ color: '#fff', fontSize: '0.9rem' }}>
            You're viewing demo data — sign up and connect your broker for live trading
          </span>
          <button
            onClick={() => {
              exitDemo();
              navigate('/onboarding');
            }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.3)',
              padding: '6px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Exit Demo
          </button>
        </div>
      )}
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Quick Trade</h1>
        <p className={styles.subtitle}>
          Buy or sell any stock / ETF instantly — no signals required.
          <Link to="/scanner" className={styles.scannerLink}>
            Want RSI signals? Use the Scanner →
          </Link>
        </p>
      </div>

      {/* Account badge */}
      <div className={`${styles.modeBadge} ${isLive ? styles.modeLive : styles.modePaper}`}>
        {isLive ? '🔴 LIVE' : '🟢 PAPER'} Account
        {account && (
          <span className={styles.modeInfo}>
            {' '}
            · Buying Power: ${Number(account.buying_power).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        )}
      </div>

      {deepLinkAction && (
        <div className={styles.deepLinkBanner}>
          <strong>Pre-filled from link:</strong> {action === 'buy' ? 'Buy' : action === 'short' ? 'Short' : 'Close'}{' '}
          {ticker}
          {qty > 0 && ` x${qty}`}
          <span className={styles.deepLinkHint}> — Review details below before executing.</span>
          <button className={styles.deepLinkDismiss} onClick={() => setDeepLinkAction(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div className={styles.layout}>
        {/* ── Left: Trade Form ── */}
        <div className={styles.tradeCol}>
          {/* Ticker Input */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Select Ticker</h2>
            <input
              className={styles.tickerInput}
              value={ticker}
              onChange={(e) => {
                setTicker(e.target.value.toUpperCase());
                setConfirming(false);
                setAnalysis(null);
                setSecondOpinion(null);
                setCounterAnalysis(null);
                setCounterOpinion(null);
              }}
              placeholder="Type a ticker symbol — e.g. XLE, SPY, AAPL"
              autoFocus
              title="Ticker symbol"
            />
            {/* Quick ticker groups — collapsible dropdowns */}
            <div className={styles.tickerGroups}>
              {Object.entries(QUICK_TICKERS).map(([key, group]) => (
                <details key={key} className={styles.tickerGroup}>
                  <summary className={styles.groupLabel}>
                    {group.label}
                    <span className={styles.groupCount}>{group.tickers.length}</span>
                    <span className={styles.groupArrow} />
                  </summary>
                  <div className={styles.chipRow}>
                    {group.tickers.map((t) => (
                      <button
                        key={t}
                        className={`${styles.tickerChip} ${ticker === t ? styles.tickerChipActive : ''}`}
                        onClick={() => selectTicker(t)}
                        title={TICKER_DESC[t] || t}
                      >
                        {t}
                        {TICKER_DESC[t] && <span className={styles.tickerDesc}>{TICKER_DESC[t]}</span>}
                      </button>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          </div>

          {/* Price Display */}
          {ticker.trim() && (
            <div className={styles.card}>
              <div className={styles.priceRow}>
                <span className={styles.priceSymbol}>{ticker}</span>
                {fetchingPrice && !price ? (
                  <span className={styles.priceLoading}>Loading...</span>
                ) : priceError ? (
                  <span className={styles.priceError}>{priceError}</span>
                ) : price !== null ? (
                  <div className={styles.priceDisplay}>
                    <span className={styles.priceValue}>${price.toFixed(2)}</span>
                    {change !== null && (
                      <span
                        className={styles.priceChange}
                        style={{ color: change >= 0 ? 'var(--color-positive)' : 'var(--color-negative)' }}
                      >
                        {change >= 0 ? '+' : ''}
                        {change.toFixed(2)}%
                      </span>
                    )}
                  </div>
                ) : null}
              </div>
              {TICKER_DESC[ticker.trim()] && <p className={styles.selectedTickerDesc}>{TICKER_DESC[ticker.trim()]}</p>}
            </div>
          )}

          {/* Side + Amount */}
          {price !== null && ticker.trim() && (
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Order Details</h2>

              {/* Position context */}
              {ticker.trim() && (
                <div className={styles.positionContext}>
                  {currentPosition ? (
                    <span className={positionSide === 'long' ? styles.posContextLong : styles.posContextShort}>
                      You hold {Math.abs(positionQty)} shares {positionSide?.toUpperCase()}
                    </span>
                  ) : (
                    <span className={styles.posContextNone}>No position in {ticker}</span>
                  )}
                </div>
              )}

              {/* Buy / Sell / Short — 3 distinct actions */}
              <div className={styles.sideToggle}>
                <button
                  className={`${styles.sideBtn} ${action === 'buy' ? styles.sideBuy : ''}`}
                  onClick={() => {
                    setAction('buy');
                    setConfirming(false);
                    setAnalysis(null);
                    setSecondOpinion(null);
                    setCounterAnalysis(null);
                    setCounterOpinion(null);
                  }}
                >
                  Buy<span className={styles.sideBtnSub}>Open long position</span>
                </button>
                <button
                  className={`${styles.sideBtn} ${action === 'sell' ? styles.sideSellClose : ''}`}
                  onClick={() => {
                    setAction('sell');
                    setConfirming(false);
                    setAnalysis(null);
                    setSecondOpinion(null);
                    setCounterAnalysis(null);
                    setCounterOpinion(null);
                  }}
                  disabled={!currentPosition || positionSide !== 'long'}
                  title={
                    !currentPosition
                      ? 'No long position to close'
                      : positionSide !== 'long'
                        ? 'Not a long position'
                        : 'Close your long position'
                  }
                >
                  Close Long<span className={styles.sideBtnSub}>Close existing position</span>
                </button>
                <button
                  className={`${styles.sideBtn} ${action === 'short' ? styles.sideShort : ''}`}
                  onClick={() => {
                    setAction('short');
                    setConfirming(false);
                    setAnalysis(null);
                    setSecondOpinion(null);
                    setCounterAnalysis(null);
                    setCounterOpinion(null);
                  }}
                >
                  Short<span className={styles.sideBtnSub}>Open short position</span>
                </button>
              </div>

              {/* Order type — Market / Limit / Stop / Stop-Limit / Trailing */}
              <div className={styles.orderTypeToggle} style={{ overflowX: 'auto' }}>
                {(
                  [
                    { key: 'market', label: 'Market' },
                    { key: 'limit', label: 'Limit' },
                    { key: 'stop', label: 'Stop' },
                    { key: 'stop_limit', label: 'Stop-Limit' },
                    { key: 'trailing_stop', label: 'Trailing' },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.key}
                    className={`${styles.orderTypeBtn} ${tradeType === opt.key ? styles.orderTypeActive : ''}`}
                    onClick={() => setTradeType(opt.key)}
                    title={opt.label}
                    style={{ minWidth: 70 }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Conditional inputs per order type */}
              {(tradeType === 'limit' || tradeType === 'stop_limit') && (
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                    Limit Price
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={limitPrice || ''}
                    onChange={(e) => setLimitPrice(parseFloat(e.target.value) || 0)}
                    placeholder={`Current: $${price?.toFixed(2)}`}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: 16,
                      borderRadius: 6,
                      border: '1px solid var(--border-primary)',
                      background: 'var(--bg-input)',
                      color: 'var(--text-primary)',
                    }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    Order fills only at this price or better.
                  </div>
                </div>
              )}

              {(tradeType === 'stop' || tradeType === 'stop_limit') && (
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                    Stop Price (trigger)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={stopPrice || ''}
                    onChange={(e) => setStopPrice(parseFloat(e.target.value) || 0)}
                    placeholder={`Current: $${price?.toFixed(2)}`}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: 16,
                      borderRadius: 6,
                      border: '1px solid var(--border-primary)',
                      background: 'var(--bg-input)',
                      color: 'var(--text-primary)',
                    }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    {tradeType === 'stop'
                      ? 'Order converts to a market order when price crosses this level.'
                      : 'Triggers a limit order at the limit price above when price crosses this level.'}
                  </div>
                </div>
              )}

              {tradeType === 'trailing_stop' && (
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                    Trail by
                  </label>
                  <div className={styles.orderTypeToggle} style={{ marginBottom: 6 }}>
                    <button
                      className={`${styles.orderTypeBtn} ${trailMode === 'percent' ? styles.orderTypeActive : ''}`}
                      onClick={() => setTrailMode('percent')}
                    >
                      %
                    </button>
                    <button
                      className={`${styles.orderTypeBtn} ${trailMode === 'price' ? styles.orderTypeActive : ''}`}
                      onClick={() => setTrailMode('price')}
                    >
                      $
                    </button>
                  </div>
                  <input
                    type="number"
                    step={trailMode === 'percent' ? '0.1' : '0.01'}
                    min={trailMode === 'percent' ? '0.1' : '0.01'}
                    max={trailMode === 'percent' ? '50' : undefined}
                    value={trailValue || ''}
                    onChange={(e) => setTrailValue(parseFloat(e.target.value) || 0)}
                    placeholder={trailMode === 'percent' ? 'e.g. 2.5' : 'e.g. 1.00'}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: 16,
                      borderRadius: 6,
                      border: '1px solid var(--border-primary)',
                      background: 'var(--bg-input)',
                      color: 'var(--text-primary)',
                    }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    {trailMode === 'percent'
                      ? `Stop trails ${trailValue || 'X'}% behind the high-water mark — fires market sell when retraced.`
                      : `Stop trails $${trailValue || 'X'} behind the high-water mark — fires market sell when retraced.`}
                  </div>
                </div>
              )}

              {/* Notional vs Qty */}
              <div className={styles.orderTypeToggle}>
                <button
                  className={`${styles.orderTypeBtn} ${orderType === 'notional' ? styles.orderTypeActive : ''}`}
                  onClick={() => setOrderType('notional')}
                >
                  $ Amount
                </button>
                <button
                  className={`${styles.orderTypeBtn} ${orderType === 'qty' ? styles.orderTypeActive : ''}`}
                  onClick={() => setOrderType('qty')}
                >
                  # Shares
                </button>
              </div>

              {orderType === 'notional' ? (
                <div className={styles.amountRow}>
                  <label className={styles.amountLabel}>Amount ($)</label>
                  <input
                    type="number"
                    className={styles.amountInput}
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    min={1}
                    max={50000}
                    step={50}
                    title="Trade amount in dollars"
                  />
                  {estimatedShares && <span className={styles.amountInfo}>≈ {estimatedShares} shares</span>}
                </div>
              ) : (
                <div className={styles.amountRow}>
                  <label className={styles.amountLabel}>Shares</label>
                  <input
                    type="number"
                    className={styles.amountInput}
                    value={qty}
                    onChange={(e) => setQty(Number(e.target.value))}
                    min={1}
                    title="Number of shares"
                    max={10000}
                    step={1}
                  />
                  {estimatedCost && <span className={styles.amountInfo}>≈ ${estimatedCost}</span>}
                </div>
              )}

              {/* Quick amounts */}
              <div className={styles.quickAmounts}>
                {[100, 250, 500, 1000, 2500, 5000].map((amt) => (
                  <button
                    key={amt}
                    className={`${styles.quickAmountBtn} ${orderType === 'notional' && amount === amt ? styles.quickAmountActive : ''}`}
                    onClick={() => {
                      setOrderType('notional');
                      setAmount(amt);
                    }}
                  >
                    ${amt.toLocaleString()}
                  </button>
                ))}
              </div>

              {/* Execute */}
              {!confirming ? (
                <button
                  className={`${styles.executeBtn} ${
                    action === 'buy' ? styles.executeBuy : action === 'sell' ? styles.executeClose : styles.executeSell
                  }`}
                  onClick={() => setConfirming(true)}
                  disabled={!isAdmin}
                >
                  {!isAdmin ? 'Admin Required' : `${actionLabel} ${ticker}`}
                </button>
              ) : (
                <div className={styles.confirmBox}>
                  <div className={styles.confirmSummary}>
                    <strong>
                      {actionLabel.toUpperCase()} {ticker}
                    </strong>
                    <span className={styles.confirmAction}>{actionDescription}</span>
                    <span>
                      {orderType === 'notional'
                        ? `$${amount.toLocaleString()} (≈ ${estimatedShares} shares)`
                        : `${qty} shares (≈ $${estimatedCost})`}
                    </span>
                    <span>@ ~${price.toFixed(2)} / share</span>
                    <span>
                      Order type:{' '}
                      <strong>
                        {tradeType === 'market' && 'Market'}
                        {tradeType === 'limit' && `Limit @ $${limitPrice.toFixed(2)}`}
                        {tradeType === 'stop' && `Stop @ $${stopPrice.toFixed(2)}`}
                        {tradeType === 'stop_limit' &&
                          `Stop-Limit (trigger $${stopPrice.toFixed(2)}, limit $${limitPrice.toFixed(2)})`}
                        {tradeType === 'trailing_stop' &&
                          `Trailing Stop — ${trailMode === 'percent' ? `${trailValue}%` : `$${trailValue.toFixed(2)}`}`}
                      </strong>
                    </span>
                    <span className={styles.confirmMode}>
                      Account: <strong>{isLive ? 'LIVE' : 'PAPER'}</strong>
                    </span>
                  </div>

                  {/* Claude Pre-Trade Analysis */}
                  {!analysis && !analyzingTrade && (
                    <button className={styles.askClaudeBtn} onClick={handleAskClaude}>
                      🤖 Ask Claude
                    </button>
                  )}
                  {analyzingTrade && (
                    <div className={styles.analysisLoading}>
                      <span className={styles.spinner} /> Claude is analyzing {ticker}...
                    </div>
                  )}
                  {analysis && (
                    <div className={styles.analysisCard}>
                      <div className={styles.analysisHeader}>
                        <span
                          className={`${styles.analysisBadge} ${
                            analysis.recommendation === 'STRONG_BUY'
                              ? styles.badgeStrongBuy
                              : analysis.recommendation === 'BUY'
                                ? styles.badgeBuy
                                : analysis.recommendation === 'HOLD'
                                  ? styles.badgeHold
                                  : analysis.recommendation === 'CAUTION'
                                    ? styles.badgeCaution
                                    : styles.badgeAvoid
                          }`}
                        >
                          {analysis.recommendation.replace('_', ' ')}
                        </span>
                        <span className={styles.analysisConfidence}>{analysis.confidence}% confidence</span>
                      </div>
                      <div className={styles.analysisIndicators}>
                        {analysis.rsi2 !== null && <span>RSI(2): {analysis.rsi2}</span>}
                        {analysis.rsi6 !== null && <span>RSI(6): {analysis.rsi6}</span>}
                        {analysis.rsi14 !== null && <span>RSI(14): {analysis.rsi14}</span>}
                        {analysis.shortInterest &&
                          (analysis.shortInterest.pct_float != null ||
                            analysis.shortInterest.days_to_cover != null) && (
                            <span
                              title={`Short interest: ${analysis.shortInterest.pct_float ?? '?'}% of float, ${analysis.shortInterest.days_to_cover ?? '?'}d to cover (${analysis.shortInterest.bucket})${
                                analysis.shortInterest.pct_change != null
                                  ? ` · ΔSI ${analysis.shortInterest.pct_change >= 0 ? '+' : ''}${analysis.shortInterest.pct_change}%`
                                  : ''
                              }`}
                            >
                              SI:{' '}
                              {analysis.shortInterest.pct_float != null
                                ? `${Math.round(analysis.shortInterest.pct_float)}%`
                                : analysis.shortInterest.days_to_cover != null
                                  ? `${analysis.shortInterest.days_to_cover}d`
                                  : '—'}{' '}
                              ({analysis.shortInterest.bucket})
                            </span>
                          )}
                        <span>
                          Mood: {analysis.mood_score > 0 ? '+' : ''}
                          {analysis.mood_score}
                        </span>
                        <span>Geo Risk: {analysis.geo_risk}</span>
                      </div>
                      <ul className={styles.analysisReasons}>
                        {analysis.reasons?.map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                      </ul>
                      {analysis.risk_factors?.length > 0 && (
                        <div className={styles.analysisRisks}>
                          <strong>Risks:</strong>
                          <ul>
                            {analysis.risk_factors.map((r, i) => (
                              <li key={i}>{r}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {analysis.timing && <div className={styles.analysisTiming}>⏱️ {analysis.timing}</div>}
                      {(analysis.suggested_stop_loss || analysis.suggested_take_profit) && (
                        <div className={styles.analysisSuggestions}>
                          {analysis.suggested_stop_loss && <span>SL: ${analysis.suggested_stop_loss.toFixed(2)}</span>}
                          {analysis.suggested_take_profit && (
                            <span>TP: ${analysis.suggested_take_profit.toFixed(2)}</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* GPT Second Opinion */}
                  {analysis && !secondOpinion && !fetchingOpinion && (
                    <button className={styles.askGptBtn} onClick={handleSecondOpinion}>
                      🧠 Get Second Opinion (GPT)
                    </button>
                  )}
                  {fetchingOpinion && (
                    <div className={styles.opinionLoading}>
                      <span className={styles.spinnerGreen} /> GPT is reviewing Claude's analysis...
                    </div>
                  )}
                  {secondOpinion && (
                    <div className={styles.opinionCard}>
                      <div className={styles.opinionHeader}>
                        <span
                          className={`${styles.verdictBadge} ${
                            secondOpinion.verdict === 'AGREE'
                              ? styles.verdictAgree
                              : secondOpinion.verdict === 'PARTIALLY_AGREE'
                                ? styles.verdictPartial
                                : styles.verdictDisagree
                          }`}
                        >
                          {secondOpinion.verdict === 'PARTIALLY_AGREE'
                            ? 'PARTIALLY AGREES'
                            : secondOpinion.verdict === 'AGREE'
                              ? 'AGREES'
                              : 'DISAGREES'}
                        </span>
                        <span className={styles.opinionConfidence}>{secondOpinion.confidence}% confidence</span>
                        <span className={styles.opinionModel}>GPT-4o</span>
                      </div>
                      <p className={styles.opinionSummary}>{secondOpinion.summary}</p>
                      <ul className={styles.opinionReasons}>
                        {secondOpinion.reasoning?.map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                      </ul>
                      {secondOpinion.concerns?.length > 0 && (
                        <div className={styles.opinionConcerns}>
                          <strong>Key Concerns:</strong>
                          <ul>
                            {secondOpinion.concerns.map((c, i) => (
                              <li key={i}>{c}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className={styles.opinionVerdict}>
                        {secondOpinion.would_take_trade
                          ? '\u2705 GPT would take this trade'
                          : '\u274C GPT would NOT take this trade'}
                      </div>
                    </div>
                  )}

                  {/* Counter-Trade Analysis */}
                  {analysis && !counterAnalysis && !fetchingCounter && action === 'buy' && (
                    <button className={styles.askCounterBtn} onClick={handleCounterTrade}>
                      🔄 What if I shorted instead?
                    </button>
                  )}
                  {analysis && !counterAnalysis && !fetchingCounter && action === 'short' && (
                    <button className={styles.askCounterBtn} onClick={handleCounterTrade}>
                      🔄 What if I went long instead?
                    </button>
                  )}
                  {fetchingCounter && (
                    <div className={styles.counterLoading}>
                      <span className={styles.spinnerAmber} /> Analyzing the opposite trade...
                    </div>
                  )}
                  {counterAnalysis && (
                    <div className={styles.counterSection}>
                      <div className={styles.counterHeader}>
                        🔄 {action === 'buy' ? 'SHORT' : 'LONG'} {ticker} — Counter Analysis
                      </div>

                      {/* Claude's counter analysis */}
                      <div className={styles.counterCard}>
                        <div className={styles.analysisHeader}>
                          <span
                            className={`${styles.analysisBadge} ${
                              counterAnalysis.recommendation === 'STRONG_BUY'
                                ? styles.badgeStrongBuy
                                : counterAnalysis.recommendation === 'BUY'
                                  ? styles.badgeBuy
                                  : counterAnalysis.recommendation === 'HOLD'
                                    ? styles.badgeHold
                                    : counterAnalysis.recommendation === 'CAUTION'
                                      ? styles.badgeCaution
                                      : styles.badgeAvoid
                            }`}
                          >
                            {counterAnalysis.recommendation.replace('_', ' ')}
                          </span>
                          <span className={styles.analysisConfidence}>{counterAnalysis.confidence}% confidence</span>
                          <span className={styles.counterModel}>Claude</span>
                        </div>
                        <div className={styles.analysisIndicators}>
                          {counterAnalysis.rsi2 !== null && <span>RSI(2): {counterAnalysis.rsi2}</span>}
                          {counterAnalysis.rsi6 !== null && <span>RSI(6): {counterAnalysis.rsi6}</span>}
                          {counterAnalysis.rsi14 !== null && <span>RSI(14): {counterAnalysis.rsi14}</span>}
                          <span>
                            Mood: {counterAnalysis.mood_score > 0 ? '+' : ''}
                            {counterAnalysis.mood_score}
                          </span>
                          <span>Geo Risk: {counterAnalysis.geo_risk}</span>
                        </div>
                        <ul className={styles.analysisReasons}>
                          {counterAnalysis.reasons?.map((r, i) => (
                            <li key={i}>{r}</li>
                          ))}
                        </ul>
                        {counterAnalysis.risk_factors?.length > 0 && (
                          <div className={styles.analysisRisks}>
                            <strong>Risks:</strong>
                            <ul>
                              {counterAnalysis.risk_factors.map((r, i) => (
                                <li key={i}>{r}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {(counterAnalysis.suggested_stop_loss || counterAnalysis.suggested_take_profit) && (
                          <div className={styles.analysisSuggestions}>
                            {counterAnalysis.suggested_stop_loss && (
                              <span>SL: ${counterAnalysis.suggested_stop_loss.toFixed(2)}</span>
                            )}
                            {counterAnalysis.suggested_take_profit && (
                              <span>TP: ${counterAnalysis.suggested_take_profit.toFixed(2)}</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* GPT's opinion on the counter trade */}
                      {counterOpinion && (
                        <div className={styles.counterGptCard}>
                          <div className={styles.opinionHeader}>
                            <span
                              className={`${styles.verdictBadge} ${
                                counterOpinion.verdict === 'AGREE'
                                  ? styles.verdictAgree
                                  : counterOpinion.verdict === 'PARTIALLY_AGREE'
                                    ? styles.verdictPartial
                                    : styles.verdictDisagree
                              }`}
                            >
                              {counterOpinion.verdict === 'PARTIALLY_AGREE'
                                ? 'PARTIALLY AGREES'
                                : counterOpinion.verdict === 'AGREE'
                                  ? 'AGREES'
                                  : 'DISAGREES'}
                            </span>
                            <span className={styles.opinionConfidence}>{counterOpinion.confidence}% confidence</span>
                            <span className={styles.opinionModel}>GPT-4o</span>
                          </div>
                          <p className={styles.opinionSummary}>{counterOpinion.summary}</p>
                          <ul className={styles.opinionReasons}>
                            {counterOpinion.reasoning?.map((r, i) => (
                              <li key={i}>{r}</li>
                            ))}
                          </ul>
                          {counterOpinion.concerns?.length > 0 && (
                            <div className={styles.opinionConcerns}>
                              <strong>Key Concerns:</strong>
                              <ul>
                                {counterOpinion.concerns.map((c, i) => (
                                  <li key={i}>{c}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div className={styles.opinionVerdict}>
                            {counterOpinion.would_take_trade
                              ? '\u2705 GPT would take the short'
                              : '\u274C GPT would NOT take the short'}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {action === 'short' && isLive && (
                    <div className={styles.shortWarning}>
                      ⚠️ <strong>SHORT on LIVE ACCOUNT</strong> — This opens a new short position with{' '}
                      <strong>unlimited loss potential</strong>. Make sure this is intentional.
                    </div>
                  )}

                  {isLive && (
                    <>
                      <div className={styles.liveWarning}>
                        ⚠️ This order will use <strong>REAL MONEY</strong> on your live Alpaca account!
                      </div>
                      <label className={styles.liveCheckbox}>
                        <input
                          type="checkbox"
                          checked={liveChecked}
                          onChange={(e) => setLiveChecked(e.target.checked)}
                        />
                        I understand this is a {action === 'short' ? 'real-money SHORT' : 'real-money'} trade
                      </label>
                    </>
                  )}

                  <div className={styles.confirmBtns}>
                    <button
                      className={styles.cancelBtn}
                      onClick={() => {
                        setConfirming(false);
                        setLiveChecked(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className={`${styles.confirmBtn} ${
                        action === 'buy'
                          ? styles.confirmBuy
                          : action === 'sell'
                            ? styles.confirmClose
                            : styles.confirmSell
                      }`}
                      onClick={isDemoMode ? undefined : handleExecuteGated}
                      disabled={isDemoMode || orderInProgress || (isLive && !liveChecked)}
                    >
                      {isDemoMode ? 'Sign up to trade live' : orderInProgress ? 'Placing...' : `Confirm ${actionLabel}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Right: Positions ── */}
        <div className={styles.posCol}>
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>
              Open Positions
              <span className={styles.posCount}>{positions.length}</span>
            </h2>
            {positions.length === 0 ? (
              <p className={styles.emptyPos}>No open positions</p>
            ) : (
              <div className={styles.posList}>
                {positions
                  .filter((p) => p.asset_class === 'us_equity')
                  .map((p) => {
                    const pnl = parseFloat(p.unrealized_pl);
                    const pnlPct = parseFloat(p.unrealized_plpc) * 100;
                    return (
                      <div
                        key={p.symbol}
                        className={`${styles.posItem} ${p.symbol === ticker ? styles.posItemActive : ''}`}
                        onClick={() => selectTicker(p.symbol)}
                      >
                        <div className={styles.posTop}>
                          <span className={styles.posSymbol}>{p.symbol}</span>
                          <span
                            className={`${styles.posSide} ${p.side === 'long' ? styles.posSideLong : styles.posSideShort}`}
                          >
                            {p.side}
                          </span>
                          <span
                            className={styles.posPnl}
                            style={{ color: pnl >= 0 ? 'var(--color-positive)' : 'var(--color-negative)' }}
                          >
                            {pnl >= 0 ? '+' : ''}
                            {pnl.toFixed(2)} ({pnlPct >= 0 ? '+' : ''}
                            {pnlPct.toFixed(1)}%)
                          </span>
                        </div>
                        <div className={styles.posBottom}>
                          <span>
                            {p.qty} shares @ ${parseFloat(p.avg_entry_price).toFixed(2)}
                          </span>
                          {isAdmin && (
                            <button
                              className={styles.closeBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClose(p.symbol);
                              }}
                            >
                              Close
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Short confirmation modal */}
      {showShortConfirm && (
        <div className={styles.shortModalOverlay} onClick={() => setShowShortConfirm(false)}>
          <div className={styles.shortModal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.shortModalTitle}>Confirm SHORT on Live Account</h3>
            <div className={styles.shortModalBody}>
              <p>
                You are about to <strong>open a SHORT position</strong> in <strong>{ticker}</strong> on your{' '}
                <strong>LIVE account</strong>.
              </p>
              <p className={styles.shortModalWarn}>
                Short selling has <strong>unlimited loss potential</strong>. Make sure this is intentional.
              </p>
              <div className={styles.shortModalDetails}>
                <div className={styles.shortModalRow}>
                  <span>Symbol</span>
                  <strong>{ticker}</strong>
                </div>
                <div className={styles.shortModalRow}>
                  <span>Qty</span>
                  <strong>{orderType === 'notional' ? `~${estimatedShares} shares` : `${qty} shares`}</strong>
                </div>
                <div className={styles.shortModalRow}>
                  <span>Est. Value</span>
                  <strong>${orderType === 'notional' ? amount.toLocaleString() : estimatedCost}</strong>
                </div>
              </div>
              <label className={styles.shortModalLabel}>
                Type <strong>{ticker}</strong> to confirm:
              </label>
              <input
                className={styles.shortModalInput}
                type="text"
                placeholder={ticker}
                value={shortConfirmTicker}
                onChange={(e) => setShortConfirmTicker(e.target.value.toUpperCase())}
                autoFocus
              />
            </div>
            <div className={styles.shortModalActions}>
              <button className={styles.cancelBtn} onClick={() => setShowShortConfirm(false)}>
                Cancel
              </button>
              <button
                className={styles.shortModalConfirmBtn}
                disabled={shortConfirmTicker !== ticker.trim().toUpperCase()}
                onClick={() => {
                  setShowShortConfirm(false);
                  handleExecute();
                }}
              >
                Confirm Short
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}
          onClick={() => setToast(null)}
        >
          {toast.msg}
        </div>
      )}
    </section>
  );
};

export default QuickTrade;
